import React from "react";
import Spinner from "react-bootstrap/Spinner";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { fetchOrders, fetchOrdersHistory } from "../../../Services/OrderService";
import { HistoryFilters } from "./HistoryFilters";
import { HistoryOrderItem } from "./HistoryOrderItem";
import { applyLocalFilters, orderGroup } from "./historyHelpers";
import "./OrdersHistoryRevamp.css";
import "./OrdersHistoryRevamp.mobile.css";

const emptyFilters = () => ({ days: "all", statuses: [], services: [], payments: [] });
const API_PAGE_SIZE = 10;

export const OrdersHistoryRevamp = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [search, setSearch] = React.useState("");
  const [tab, setTab] = React.useState("all");
  const [draftFilters, setDraftFilters] = React.useState(emptyFilters);
  const [filters, setFilters] = React.useState(emptyFilters);
  const [expanded, setExpanded] = React.useState(null);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [hasMore, setHasMore] = React.useState(true);
  const loadMoreRef = React.useRef(null);
  const mountedRef = React.useRef(true);
  const loadingRef = React.useRef(false);
  const sourcesRef = React.useRef({
    running: { offset: 1, done: false, ids: new Set() },
    history: { offset: 1, done: false, ids: new Set() },
  });

  const loadNextPage = React.useCallback(async () => {
    if (loadingRef.current) return;
    const sources = sourcesRef.current;
    const pending = [
      ["running", fetchOrders],
      ["history", fetchOrdersHistory],
    ].filter(([name]) => !sources[name].done);

    if (!pending.length) {
      setHasMore(false);
      return;
    }

    loadingRef.current = true;
    setLoadingMore(true);
    const results = await Promise.allSettled(pending.map(([name, request]) =>
      request({ limit: API_PAGE_SIZE, offset: sources[name].offset })
    ));

    if (!mountedRef.current) return;
    const incoming = [];
    let failed = false;
    results.forEach((result, index) => {
      const [name] = pending[index];
      const source = sources[name];
      if (result.status === "rejected") {
        source.done = true;
        failed = true;
        return;
      }

      const response = result.value || {};
      const page = Array.isArray(response.data) ? response.data : [];
      const fresh = page.filter((order) => {
        const id = String(order.id);
        if (source.ids.has(id)) return false;
        source.ids.add(id);
        return true;
      });
      incoming.push(...fresh);

      const total = Number(response.total_size);
      const reachedTotal = Number.isFinite(total) && source.ids.size >= total;
      source.done = !page.length || page.length < API_PAGE_SIZE || !fresh.length || reachedTotal;
      if (!source.done) source.offset += 1;
    });

    setOrders((current) => {
      const merged = Array.from(new Map([...current, ...incoming].map((order) => [String(order.id), order])).values());
      setExpanded((value) => value ?? merged[0]?.id ?? null);
      return merged;
    });
    if (failed) toast.error("Some orders could not be loaded.");
    setHasMore(Object.values(sources).some((source) => !source.done));
    setLoading(false);
    setLoadingMore(false);
    loadingRef.current = false;
  }, []);

  React.useEffect(() => {
    mountedRef.current = true;
    loadNextPage();
    return () => { mountedRef.current = false; };
  }, [loadNextPage]);

  React.useEffect(() => {
    if (!hasMore || loading) return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadNextPage();
    }, { rootMargin: "300px 0px" });
    const target = loadMoreRef.current;
    if (target) observer.observe(target);
    return () => observer.disconnect();
  }, [hasMore, loading, loadNextPage]);

  const counts = React.useMemo(() => orders.reduce((result, order) => ({ ...result, [orderGroup(order)]: result[orderGroup(order)] + 1 }), { in_progress: 0, completed: 0, cancelled: 0 }), [orders]);
  const filtered = React.useMemo(() => applyLocalFilters(orders, filters, tab, search), [orders, filters, tab, search]);
  const viewOrder = (order) => navigate(`/app/${order.order_status === "cancelled" ? "order-canceled" : "order-details"}/${order.id}`);

  return <main className="ohr-page"><div className="ohr-container">
    <header className="ohr-heading"><div><h1>Order History</h1><p>Track, manage and reorder your laundry services.</p></div><label><i className="fa-solid fa-magnifying-glass" /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search by Order ID" /></label></header>
    <div className="ohr-layout" style={{ gridTemplateColumns: "1fr" }}>
      {/* Filters are temporarily hidden. Keep this block for restoring them later. */}
      {false && <HistoryFilters filters={draftFilters} setFilters={setDraftFilters} onApply={() => setFilters(draftFilters)} onReset={() => { const reset = emptyFilters(); setDraftFilters(reset); setFilters(reset); }} />}
      <section className="ohr-main">
        <div className="ohr-stats"><div><i className="fa-solid fa-layer-group" /><strong>{orders.length}<small>Total Orders</small></strong></div><div><i className="fa-solid fa-spinner" /><strong>{counts.in_progress}<small>In Progress</small></strong></div><div><i className="fa-regular fa-circle-check" /><strong>{counts.completed}<small>Completed</small></strong></div><div><i className="fa-regular fa-circle-xmark" /><strong>{counts.cancelled}<small>Cancelled</small></strong></div></div>
        <nav className="ohr-tabs">{[["all","All Orders",orders.length],["in_progress","In Progress",counts.in_progress],["completed","Completed",counts.completed],["cancelled","Cancelled",counts.cancelled]].map(([value,label,count]) => <button className={tab === value ? "active" : ""} type="button" onClick={() => setTab(value)} key={value}>{label} ({count})</button>)}</nav>
        {loading ? <div className="ohr-loading"><Spinner variant="warning" /></div> : filtered.length ? <div className="ohr-orders">{filtered.map((order) => <HistoryOrderItem order={order} expanded={expanded === order.id} onToggle={() => setExpanded((current) => current === order.id ? null : order.id)} onView={() => viewOrder(order)} key={order.id} />)}</div> : <div className="ohr-empty">No orders match your search or selected tab.</div>}
        {!loading && hasMore && <div className="ohr-loading" ref={loadMoreRef}>{loadingMore && <Spinner animation="border" size="sm" variant="warning" />}</div>}
      </section>
    </div>
  </div></main>;
};

export default OrdersHistoryRevamp;
